import React, { FC, MouseEvent, useEffect, useMemo } from 'react';
import {
    Person as PersonModel,
    SelectionMovie,
} from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { AnalyticsSenderProvider, useAnalytics } from '@funtech-int/yango-shared';
import { InfinitePagination } from '@funtech-int/rival';

import * as Card from '@/shared/components/Card';
import { filmRoute } from '@/scenarios/movie/routes';
import { useMovieModal } from '@/shared/hooks/useMovieModal';
import { InfiniteGrid } from '@/shared/components/Grid/InfiniteGrid';
import { clientOnlyPage } from '@/shared/lib/clientOnly';
import { AutoOpenPaymentWidget } from '@/shared/components/AutoOpenPaymentWidget/AutoOpenPaymentWidget';

import { usePersonQuery } from '../../hooks/usePersonQuery';
import { createAnalyticsEvents } from '../../lib/createAnalyticsEvents';
import { PersonTitle } from '../PersonTitle/PersonTitle';
import { PersonHead } from '../PersonHead/PersonHead';
import { PlayerManager } from '../../../legacy-player/components/PlayerManager/PlayerManagerMain';

interface PersonContainerProps {
    person: PersonModel;
    pagination: InfinitePagination;
}

const PersonContainer: FC<PersonContainerProps> = ({ person, pagination }) => {
    const analytics = useAnalytics(createAnalyticsEvents);

    useEffect(() => {
        analytics.showed(person.id);
    }, [analytics, person.id]);

    const movieModal = useMovieModal();

    const { getKey, renderItem } = useMemo(() => {
        const cardAnalytics = analytics.createFilmographyCardAnalyticsEvents(person);

        return {
            getKey(item: SelectionMovie) {
                return item.contentId;
            },

            renderItem(item: SelectionMovie, index: number) {
                const handleShow = () => {
                    cardAnalytics.cardViewed(index, item.contentId, item.title.toString());
                };

                const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
                    e.preventDefault();
                    movieModal.open(item.contentId);
                };

                return (
                    <Card.Movie
                        movie={item}
                        location={filmRoute({ id: item.contentId })}
                        onShow={handleShow}
                        onClick={handleClick}
                        withPlannedToWatch
                    />
                );
            },
        };
    }, [analytics, movieModal, person]);

    return (
        <InfiniteGrid
            items={person.filmography.items}
            getKey={getKey}
            renderItem={renderItem}
            pagination={pagination}
            title={<PersonTitle name={person.name} poster={person.poster?.toString()} />}
        />
    );
};

const PersonContent = clientOnlyPage(({ person, pagination }: PersonContainerProps) => (
    <>
        <PlayerManager />
        <AnalyticsSenderProvider>
            <PersonContainer person={person} pagination={pagination} />
            <AutoOpenPaymentWidget />
        </AnalyticsSenderProvider>
    </>
));

export const Person: FC = () => {
    const { person, pagination } = usePersonQuery();

    return (
        <>
            <PersonHead person={person} />
            <PersonContent person={person} pagination={pagination} />
        </>
    );
};

export default Person;
